import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

interface CycleFormData {
	name: string
	launchDate: string
	previewDate: string
	deadline?: string | null
	isTemplate?: boolean
}

interface CycleData {
	id: string
	name: string
	launchDate: string | null
	previewDate: string | null
	deadline: string | null
	status: 'in-progress' | 'draft' | 'ready-for-launch' | 'completed'
	hierarchyFileId: string | null
	organizationId: string
	isTemplate: boolean
}

interface CycleState {
	cycle: CycleData | null
	isLoading: boolean
	error: string | null

	setCycle: (cycle: CycleData) => void
	resetCycle: () => void
	saveCycle: () => CycleFormData
	updateCycle: (data: Partial<CycleFormData>) => void
	setError: (error: string | null) => void

	useCreateCycle: () => {
		createCycle: (data: CycleFormData) => Promise<void>
		isLoading: boolean
	}
	useEditCycle: (cycleId?: string) => {
		editCycle: (data: CycleFormData) => Promise<void>
		isLoading: boolean
	}
}

export const useCycleStore = create<CycleState>()(
	devtools(
		immer((set, get) => ({
			cycle: null,
			isLoading: false,
			error: null,

			setCycle: (cycle: CycleData) =>
				set((state) => {
					state.cycle = cycle
				}),

			resetCycle: () =>
				set((state) => {
					state.cycle = null
					state.error = null
				}),

			updateCycle: (data: Partial<CycleFormData>) =>
				set((state) => {
					if (state.cycle) {
						state.cycle = {
							...state.cycle,
							...data,
						}
					} else {
						state.cycle = {
							id: '',
							name: '',
							launchDate: null,
							previewDate: null,
							deadline: null,
							status: 'draft',
							hierarchyFileId: null,
							organizationId: '',
							isTemplate: false,
							...data,
						}
					}
				}),

			setError: (error: string | null) =>
				set((state) => {
					state.error = error
				}),

			saveCycle: () => {
				const state = get()
				if (!state.cycle) throw new Error('No cycle data to save')
				return {
					name: state.cycle.name,
					launchDate: state.cycle.launchDate || '',
					previewDate: state.cycle.previewDate || '',
				}
			},

			useCreateCycle: () => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const navigate = useNavigate()
				const cycle = get().cycle

				const createCycle = useMutation({
					...trpc.admin.cycles.create.mutationOptions(),
					onSuccess: (data) => {
						toast.showToast('Cycle created successfully')
						if (cycle?.organizationId) {
							navigate(`/organizations/${cycle.organizationId}/cycles/${data.id}/general`)
						}
					},
					onError: () => {
						toast.showToast('Error creating cycle', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['cycles']] })
					},
				})

				return {
					createCycle: async (data: CycleFormData) => {
						if (!cycle?.organizationId) return
						await createCycle.mutateAsync({
							organizationId: cycle.organizationId,
							...data,
							metadata: null,
							triggerLogId: null,
						})
					},
					isLoading: createCycle.isPending,
				}
			},

			useEditCycle: (cycleId?: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const cycle = get().cycle

				const editMutation = useMutation({
					...trpc.admin.cycles.edit.mutationOptions(),
					onSuccess: () => {
						toast.showToast('Cycle updated successfully')
					},
					onError: () => {
						toast.showToast('Error updating cycle', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.admin.cycles.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.cycles.getById.queryKey({ id: cycleId }),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					editCycle: async (data: CycleFormData) => {
						if (!cycle?.organizationId || !cycleId) return
						await editMutation.mutateAsync({
							...data,
							id: cycleId,
							organizationId: cycle.organizationId,
							metadata: null,
							triggerLogId: null,
						})
					},
					isLoading: editMutation.isPending,
				}
			},
		})),
		{ name: 'cycle-store' },
	),
)
